/**
 * PRISM compliance FACTS for display (decided 2026-10-29: no compliance score outside SPRS).
 *
 * The backend sends `compliance_facts` (backend `services/prismComplianceFacts.js`, PRISM #291
 * contract), pooled across the org's PRISM projects. Screens show open gaps (critical where
 * known) and % of applicable controls evaluated — "% evaluated" is progress, not compliance.
 *
 * ⛔ Anything other than `status: 'measured'` is "Not measured": an old-shape snapshot (written
 * before PRISM's refresh script ran), a failed fetch, no standards, or an older backend that
 * sends no facts at all. Never a percentage, never a 0.
 */

export const NOT_MEASURED = 'Not measured';

export interface ComplianceFacts {
  status: string;
  measured?: boolean;
  standards_tracked: number | null;
  applicable: number | null;
  evaluated: number | null;
  not_assessed?: number | null;
  open_gaps: number | null;
  critical_open_gaps: number | null;
  evaluated_pct: number | null;
}

interface MeasuredFacts {
  standardsTracked: number;
  applicable: number;
  evaluated: number;
  openGaps: number;
  criticalOpenGaps: number | null;
  evaluatedPct: number | null;
}

function isCount(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v) && v >= 0;
}

/** The facts, or null when they are not measured (or not well-formed). */
export function readMeasuredFacts(facts: unknown): MeasuredFacts | null {
  if (!facts || typeof facts !== 'object') return null;
  const f = facts as Partial<ComplianceFacts>;
  if (f.status !== 'measured') return null;
  if (!isCount(f.standards_tracked) || !isCount(f.applicable) || !isCount(f.evaluated) || !isCount(f.open_gaps)) {
    return null;
  }
  if (f.standards_tracked === 0 || f.applicable === 0) return null;
  return {
    standardsTracked: f.standards_tracked,
    applicable: f.applicable,
    evaluated: f.evaluated,
    openGaps: f.open_gaps,
    criticalOpenGaps: isCount(f.critical_open_gaps) ? f.critical_open_gaps : null,
    evaluatedPct: typeof f.evaluated_pct === 'number' && Number.isFinite(f.evaluated_pct) ? f.evaluated_pct : null,
  };
}

/** Floors, so an unfinished assessment never reads 100%; a sliver reads "<1%", not 0%. */
export function formatEvaluatedPct(pct: number | null | undefined): string | null {
  if (typeof pct !== 'number' || !Number.isFinite(pct)) return null;
  if (pct > 0 && pct < 1) return '<1%';
  return `${Math.floor(pct)}%`;
}

export interface ComplianceFactsDisplay {
  measured: boolean;
  /** Headline open-gaps value, or "Not measured". */
  openGaps: string;
  /** "1 critical" when known, else ''. */
  criticalDetail: string;
  /** "74%" evaluated, or "Not measured". */
  evaluated: string;
  /** "20 of 27 applicable controls evaluated", or ''. */
  evaluatedDetail: string;
  /** For a progress bar; null when not measured. */
  evaluatedPct: number | null;
  standardsTracked: number | null;
}

export function complianceFactsDisplay(facts: unknown): ComplianceFactsDisplay {
  const m = readMeasuredFacts(facts);
  if (!m) {
    return {
      measured: false,
      openGaps: NOT_MEASURED,
      criticalDetail: '',
      evaluated: NOT_MEASURED,
      evaluatedDetail: '',
      evaluatedPct: null,
      standardsTracked: null,
    };
  }
  const evaluated = formatEvaluatedPct(m.evaluatedPct);
  return {
    measured: true,
    openGaps: String(m.openGaps),
    // Includes critical controls not yet assessed (PRISM B2): say so, never a bare "N critical".
    criticalDetail:
      m.criticalOpenGaps != null
        ? `${m.criticalOpenGaps} critical control${m.criticalOpenGaps === 1 ? '' : 's'} not yet shown met`
        : '',
    evaluated: evaluated ?? NOT_MEASURED,
    evaluatedDetail: `${m.evaluated} of ${m.applicable} applicable control${m.applicable === 1 ? '' : 's'} evaluated`,
    evaluatedPct: evaluated ? m.evaluatedPct : null,
    standardsTracked: m.standardsTracked,
  };
}

const CMMC_HEADING_LABELS = new Set(['CMMC Level 1', 'CMMC Level 2', 'CMMC Level 3']);

/**
 * The CMMC readiness heading's label: the narrative's `cmmcLabel` ("CMMC Level 1/2/3", from
 * PRISM's target_level) when it is one of those, else plain "CMMC" — an older backend sends no
 * label, and the level is never guessed from the counts.
 */
export function cmmcHeadingLabel(narrative: { cmmcLabel?: string | null } | null | undefined): string {
  const label = narrative?.cmmcLabel;
  return typeof label === 'string' && CMMC_HEADING_LABELS.has(label) ? label : 'CMMC';
}
